import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import BackToTop from '@/components/feature/BackToTop';
import faqSections from './faqData';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

/* ────────────── DATA ────────────── */

const eWasteTypes = [
  {
    title: 'Large Household Appliances',
    examples: 'Refrigerators, washing machines, dryers, dishwashers, air conditioners, ovens',
    materials: 'Steel, aluminum, copper, plastics, refrigerants, insulation foam',
    icon: 'ri-fridge-line',
  },
  {
    title: 'IT & Telecommunications Equipment',
    examples: 'Computers, laptops, tablets, servers, routers, switches, printers, monitors',
    materials: 'Precious metals (gold, silver), copper, rare earth elements, plastics',
    icon: 'ri-computer-line',
  },
  {
    title: 'Consumer Electronics',
    examples: 'Televisions, radios, cameras, video recorders, audio equipment, gaming consoles',
    materials: 'Glass, plastics, copper, gold, cadmium, lead',
    icon: 'ri-tv-2-line',
  },
  {
    title: 'Lighting Equipment',
    examples: 'Fluorescent lamps, LED bulbs, high-intensity discharge lamps, compact fluorescents',
    materials: 'Mercury, phosphor powders, glass, aluminum, plastics',
    icon: 'ri-lightbulb-line',
  },
  {
    title: 'Electronic Tools',
    examples: 'Drills, saws, sewing machines, lawn equipment, welding equipment',
    materials: 'Steel, copper, batteries, plastics, small motors',
    icon: 'ri-tools-line',
  },
  {
    title: 'Toys, Leisure & Sports',
    examples: 'Electric trains, video game consoles, sports equipment with electronics, calculators',
    materials: 'Plastics, batteries, small circuit boards, LCD screens',
    icon: 'ri-gamepad-line',
  },
  {
    title: 'Medical Devices',
    examples: 'Patient monitors, dialysis machines, radiotherapy equipment, laboratory analyzers',
    materials: 'Steel, precious metals, batteries, sensors, plastics',
    icon: 'ri-heart-pulse-line',
  },
  {
    title: 'Monitoring & Control Equipment',
    examples: 'Smoke detectors, thermostats, heating regulators, metering devices, scales',
    materials: 'Circuit boards, batteries, sensors, plastics, lead-acid batteries',
    icon: 'ri-dashboard-line',
  },
  {
    title: 'Batteries & Power Storage',
    examples: 'Lithium-ion batteries, lead-acid batteries, nickel-cadmium, UPS units, EV batteries',
    materials: 'Lithium, cobalt, nickel, lead, acid, cadmium, rare earths',
    icon: 'ri-battery-charge-line',
  },
  {
    title: 'Cables, Connectors & Accessories',
    examples: 'USB cables, HDMI cables, power cords, chargers, adapters, extension cords',
    materials: 'Copper, aluminum, PVC plastics, rubber, gold-plated connectors',
    icon: 'ri-plug-line',
  },
];

const recyclingSteps = [
  {
    step: '1',
    title: 'Collection & Drop-Off',
    desc: 'Bring your electronics to a CalRecycle-certified facility or schedule a free business pickup. Greenly accepts drop-offs at two LA locations with no appointment needed.',
    icon: 'ri-map-pin-line',
  },
  {
    step: '2',
    title: 'Sorting & Assessment',
    desc: 'Items are sorted by type and material. Working devices are set aside for refurbishment. Non-working devices are prepared for responsible dismantling.',
    icon: 'ri-list-check',
  },
  {
    step: '3',
    title: 'Data Destruction',
    desc: 'Hard drives and storage devices are wiped to DoD 5220.22-M standards or physically shredded. You receive a Certificate of Data Destruction for your records.',
    icon: 'ri-shield-check-line',
  },
  {
    step: '4',
    title: 'Dismantling & Separation',
    desc: 'Devices are manually and mechanically dismantled. Plastics, metals, glass, and circuit boards are separated using magnets, eddy currents, and water baths.',
    icon: 'ri-hammer-line',
  },
  {
    step: '5',
    title: 'Material Recovery',
    desc: 'Precious metals — gold, silver, copper, palladium, and rare earth elements — are recovered through specialized smelting and chemical processes.',
    icon: 'ri-coins-line',
  },
  {
    step: '6',
    title: 'Safe Disposal',
    desc: 'Hazardous materials like leaded glass, mercury, and cadmium are disposed of according to EPA and CalRecycle regulations. Nothing goes to a landfill.',
    icon: 'ri-shield-flash-line',
  },
];

const environmentalImpacts = [
  {
    stat: '62M',
    label: 'Metric Tons of E-Waste Generated Globally in 2024',
    icon: 'ri-global-line',
  },
  {
    stat: '18%',
    label: 'Percentage of E-Waste Formally Recycled Worldwide',
    icon: 'ri-recycle-line',
  },
  {
    stat: '7.6M',
    label: 'Metric Tons of E-Waste Generated by the U.S. Annually',
    icon: 'ri-building-2-line',
  },
  {
    stat: '$62B',
    label: 'Value of Raw Materials Lost in Unrecycled E-Waste Each Year',
    icon: 'ri-money-dollar-circle-line',
  },
];

/* ────────────── COMPONENTS ────────────── */

const GuideJsonLd = () => (
  <script type="application/ld+json">
    {JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/e-waste-guide#webpage`,
          url: `${SITE_URL}/e-waste-guide`,
          name: 'What Is E-Waste? Types, Laws & Recycling Guide | Greenly Los Angeles',
          description: 'Learn what e-waste is, the 10 types of electronic waste, California e-waste laws (SB 20), environmental impact, and how to recycle responsibly in Los Angeles.',
          isPartOf: { '@id': `${SITE_URL}/#website` },
          about: { '@id': `${SITE_URL}/#business` },
          breadcrumb: { '@id': `${SITE_URL}/e-waste-guide#breadcrumb` },
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${SITE_URL}/e-waste-guide#breadcrumb`,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'E-Waste Guide & Glossary', item: `${SITE_URL}/e-waste-guide` },
          ],
        },
        {
          '@type': 'FAQPage',
          '@id': `${SITE_URL}/e-waste-guide#faqpage`,
          mainEntity: faqSections.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        },
      ],
    })}
  </script>
);

const SectionHeading = ({ eyebrow, title, subtitle }: { eyebrow?: string; title: string; subtitle?: string }) => (
  <div className="text-center mb-10 md:mb-12">
    {eyebrow && (
      <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-3">
        {eyebrow}
      </div>
    )}
    <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3 leading-tight">
      {title}
    </h2>
    {subtitle && (
      <p className="text-gray-500 text-sm max-w-2xl mx-auto leading-relaxed">
        {subtitle}
      </p>
    )}
  </div>
);

const DefinitionCard = ({ term, definition, highlight = false }: { term: string; definition: string; highlight?: boolean }) => (
  <div className={`border rounded-xl p-5 ${highlight ? 'border-[#1B6B3A]/30 bg-[#F4FAF7]' : 'border-gray-200 bg-white'}`}>
    <dt className="font-extrabold text-gray-900 text-sm mb-2">{term}</dt>
    <dd className="text-gray-600 text-sm leading-relaxed">{definition}</dd>
  </div>
);

const FAQAccordion = () => {
  const [openId, setOpenId] = useState<string | null>(null);
  const toggle = (id: string) => setOpenId(openId === id ? null : id);

  return (
    <div className="max-w-4xl mx-auto space-y-3">
      {faqSections.map((faq) => {
        const isOpen = openId === faq.id;
        return (
          <div key={faq.id} className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left cursor-pointer hover:bg-[#F4FAF7] transition-colors"
              onClick={() => toggle(faq.id)}
            >
              <span className="text-sm font-bold text-gray-900 leading-snug">{faq.question}</span>
              <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                <i className={`text-[#1B6B3A] text-base transition-transform duration-200 ${isOpen ? 'ri-subtract-line' : 'ri-add-line'}`} />
              </div>
            </button>
            {isOpen && (
              <div className="px-5 pb-5 pt-1 bg-[#F4FAF7]">
                <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ────────────── PAGE ────────────── */

const EwasteGuidePage = () => {
  return (
    <>
      <title>What Is E-Waste? Types, Laws &amp; Recycling Guide | Greenly Los Angeles</title>
      <meta name="description" content="Learn what e-waste is, the 10 types of electronic waste, California e-waste recycling laws (SB 20), environmental impact, and how to recycle responsibly in Los Angeles. CalRecycle certified CEWD-127531." />
      <meta name="keywords" content="what is e-waste, types of electronic waste, e-waste laws California, e-waste recycling guide, e-waste definition, electronic waste Los Angeles, how to recycle e-waste, California SB 20, covered electronic devices, e-waste environmental impact" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/e-waste-guide`} />

      {/* Open Graph */}
      <meta property="og:title" content="What Is E-Waste? Types, Laws & Recycling Guide | Greenly Los Angeles" />
      <meta property="og:description" content="Learn what e-waste is, the 10 types of electronic waste, California e-waste recycling laws, environmental impact, and how to recycle responsibly in LA." />
      <meta property="og:image" content="/images/greenly.png" />
      <meta property="og:url" content={`${SITE_URL}/e-waste-guide`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Greenly Electronics Recycling" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="What Is E-Waste? Types, Laws & Recycling Guide | Greenly Los Angeles" />
      <meta name="twitter:description" content="Learn what e-waste is, the 10 types of electronic waste, California e-waste recycling laws, environmental impact, and how to recycle responsibly in LA." />
      <meta name="twitter:image" content="/images/greenly.png" />
      <meta name="twitter:site" content="@greenlyrecycling" />

      <GuideJsonLd />
      <Navbar />
      <main>
        {/* ════════════════════════ HERO ════════════════════════ */}
        <section className="bg-[#0F3D22] pt-28 pb-14 px-4 md:px-6 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20illustration%20of%20electronic%20waste%20recycling%2C%20organized%20piles%20of%20circuit%20boards%20and%20old%20devices%20being%20processed%2C%20green%20sustainable%20environment%2C%20stylized%20modern%20artwork%20with%20warm%20earth%20tones%20and%20emerald%20green%20accents%2C%20clean%20minimal%20composition&width=1400&height=400&seq=ewaste-hero-bg&orientation=landscape"
              alt="E-waste recycling abstract illustration"
              className="w-full h-full object-cover object-center"
              loading="eager"
            />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: 'E-Waste Guide & Glossary' }]} />
            <div className="max-w-3xl mt-4">
              <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                <i className="ri-book-open-line" />
                Complete Reference Guide
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">
                What Is E-Waste? Definitions, Types &amp; California Laws
              </h1>
              <p className="text-green-200 text-base leading-relaxed max-w-2xl">
                A comprehensive guide to electronic waste: what it is, the 10 main categories, why it matters for the environment, and the California laws that protect you. Plus how to recycle e-waste responsibly in Los Angeles.
              </p>
            </div>
          </div>
        </section>

        {/* ════════════════════════ WHAT IS E-WASTE ════════════════════════ */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
              <div>
                <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-3">
                  E-Waste Definition
                </div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-5 leading-tight">
                  What Is E-Waste?
                </h2>
                <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
                  <p>
                    <strong className="text-gray-900">E-waste</strong> (electronic waste) refers to any discarded electronic or electrical device, or any device with a battery, power cord, or plug. This includes everything from large household appliances like refrigerators to small handheld devices like smartphones.
                  </p>
                  <p>
                    The term covers items that have reached the end of their useful life — whether broken, obsolete, or simply unwanted. E-waste is the fastest-growing waste stream in the world, outpacing general municipal solid waste by nearly three times.
                  </p>
                  <p>
                    In California, the legal term is <strong className="text-gray-900">"covered electronic devices" (CEDs)</strong> — specifically referring to TVs, computer monitors, laptops, and other display devices covered under the state&apos;s recycling program. The broader category of electronic waste includes many more items.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                        <i className="ri-lightbulb-line text-amber-600 text-lg" />
                      </div>
                      <div>
                        <div className="font-bold text-amber-800 text-sm mb-1">Key Fact</div>
                        <p className="text-amber-700 text-xs leading-relaxed">
                          The world generated <strong>62 million metric tons</strong> of e-waste in 2024. Less than 18% was formally collected and recycled. The rest sits in landfills, incinerators, or is informally processed in developing countries — releasing toxic chemicals into the environment.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="bg-[#F4FAF7] border border-green-100 rounded-2xl p-6">
                  <h3 className="text-lg font-extrabold text-gray-900 mb-4">
                    E-Waste Glossary: Key Terms
                  </h3>
                  <dl className="space-y-3">
                    <DefinitionCard
                      term="Covered Electronic Device (CED)"
                      definition="Under California SB 20, CEDs are TVs, computer monitors, laptops, and other display devices sold in California. Retailers must collect a recycling fee at point of sale, and consumers can return them for free recycling."
                      highlight
                    />
                    <DefinitionCard
                      term="CRT (Cathode Ray Tube)"
                      definition="The large, heavy glass tube inside older televisions and monitors. CRTs contain 4–8 pounds of lead and are classified as hazardous waste. Specialized handling is required for safe recycling."
                    />
                    <DefinitionCard
                      term="WEEE (Waste Electrical and Electronic Equipment)"
                      definition="The European Union directive that sets collection, recycling, and recovery targets for e-waste. WEEE categories form the basis of most international e-waste classification systems."
                    />
                    <DefinitionCard
                      term="Rare Earth Elements (REEs)"
                      definition="A group of 17 metallic elements including neodymium, dysprosium, and lanthanum. They are essential for electronics manufacturing and are recovered during advanced e-waste recycling processes."
                    />
                    <DefinitionCard
                      term="E-Stewards Certification"
                      definition="A voluntary certification program for e-waste recyclers that prohibits export to developing countries, requires maximum reuse, and bans prison labor. Considered the gold standard in ethical e-waste recycling."
                    />
                    <DefinitionCard
                      term="CalRecycle CEWD"
                      definition="California Department of Resources Recycling and Recovery — the state agency that certifies e-waste collection sites and processors. A CEWD certification number (like CEWD-127531) proves a facility meets California's strict recycling standards."
                    />
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════ TYPES OF E-WASTE ════════════════════════ */}
        <section className="py-16 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <SectionHeading
              eyebrow="10 Categories"
              title="Types of Electronic Waste"
              subtitle="Electronic waste is classified into 10 broad categories based on the EU WEEE Directive — the standard used by recyclers worldwide. Understanding these types helps you know what can be recycled and what to expect at the facility."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {eWasteTypes.map((type) => (
                <div
                  key={type.title}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#1B6B3A]/30 transition-all duration-200 group"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl mb-3 group-hover:bg-[#1B6B3A] transition-colors">
                    <i className={`${type.icon} text-[#1B6B3A] text-lg group-hover:text-white transition-colors`} />
                  </div>
                  <h3 className="text-sm font-extrabold text-gray-900 mb-2 leading-snug">
                    {type.title}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-3">
                    <strong className="text-gray-700">Examples:</strong> {type.examples}
                  </p>
                  <p className="text-gray-400 text-xs leading-relaxed">
                    <strong className="text-gray-600">Materials:</strong> {type.materials}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════ CALIFORNIA E-WASTE LAWS ════════════════════════ */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <SectionHeading
              eyebrow="California Regulations"
              title="E-Waste Laws in California"
              subtitle="California leads the nation in e-waste regulation with some of the most comprehensive laws in the United States. Here's what residents and businesses need to know."
            />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* SB 20 */}
              <div className="bg-[#F4FAF7] border border-green-100 rounded-2xl p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-[#1B6B3A] rounded-xl mb-4">
                  <i className="ri-file-list-3-line text-white text-xl" />
                </div>
                <h3 className="text-lg font-extrabold text-gray-900 mb-2">
                  SB 20 — The Electronic Waste Recycling Act of 2003
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  California's landmark e-waste law requires retailers to collect an <strong className="text-gray-900">Advanced Recycling Fee (ARF)</strong> when selling covered electronic devices (CEDs). That fee funds a statewide network of free collection and recycling centers.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  <strong className="text-gray-900">What it covers:</strong> Televisions, computer monitors, laptops, portable DVD players, and other display devices.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  <strong className="text-gray-900">What it means for you:</strong> When you buy a TV or laptop in California, you already paid for its end-of-life recycling. You can drop it off at any CalRecycle-certified facility — like Greenly — at no additional cost.
                </p>
              </div>

              {/* Illegal Disposal */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-red-500 rounded-xl mb-4">
                  <i className="ri-prohibited-line text-white text-xl" />
                </div>
                <h3 className="text-lg font-extrabold text-gray-900 mb-2">
                  Illegal Disposal of Electronics
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Under California law, it is <strong className="text-gray-900">illegal</strong> to dispose of covered electronic devices (TVs, monitors, laptops) in regular trash or landfill. These items contain hazardous materials that require specialized handling.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  <strong className="text-gray-900">Penalties:</strong> Violators can face fines under California's hazardous waste regulations. Businesses disposing of electronics improperly risk significant EPA and CalRecycle penalties.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  <strong className="text-gray-900">How to comply:</strong> Always use a certified e-waste recycler. Greenly is CalRecycle certified (CEWD-127531) and accepts drop-offs with no appointment at either LA location.
                </p>
              </div>

              {/* Right to Repair */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-[#4CAF82] rounded-xl mb-4">
                  <i className="ri-tools-line text-white text-xl" />
                </div>
                <h3 className="text-lg font-extrabold text-gray-900 mb-2">
                  Right to Repair &amp; Extended Producer Responsibility
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  California passed the <strong className="text-gray-900">Right to Repair Act (SB 244)</strong> in 2023, requiring manufacturers to make repair documentation, parts, and software available to consumers and independent repair shops.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  This extends the usable life of electronics, directly reducing e-waste generation. The law applies to smartphones, laptops, and other consumer electronics sold after July 2024.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  <strong className="text-gray-900">The bigger picture:</strong> Extended Producer Responsibility (EPR) laws shift accountability from consumers to manufacturers, incentivizing longer-lasting, easier-to-recycle product design.
                </p>
              </div>
            </div>

            {/* Law comparison table */}
            <div className="mt-10 bg-[#F4FAF7] border border-green-100 rounded-2xl overflow-hidden">
              <div className="bg-[#1B6B3A] px-6 py-4">
                <h3 className="text-white font-extrabold text-base">California vs. Federal E-Waste Regulation</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-green-100">
                      <th className="text-left px-6 py-3 font-extrabold text-gray-700">Aspect</th>
                      <th className="text-left px-6 py-3 font-extrabold text-[#1B6B3A]">California</th>
                      <th className="text-left px-6 py-3 font-extrabold text-gray-500">Federal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { aspect: 'E-waste landfill ban', ca: 'Yes — CEDs prohibited in trash', fed: 'No federal ban' },
                      { aspect: 'Recycling fee at sale', ca: 'Yes — ARF on TVs, monitors, laptops', fed: 'No federal fee' },
                      { aspect: 'Free drop-off for consumers', ca: 'Yes — funded by ARF', fed: 'No federal program' },
                      { aspect: 'Producer responsibility', ca: 'Growing — Right to Repair passed', fed: 'Limited' },
                      { aspect: 'Certification for recyclers', ca: 'CalRecycle CEWD required', fed: 'R2 / e-Stewards voluntary' },
                      { aspect: 'Data privacy in recycling', ca: 'CalRecycle requires chain of custody', fed: 'HIPAA (healthcare only)' },
                    ].map((row, i) => (
                      <tr key={i} className={`border-b border-green-50 ${i % 2 === 1 ? 'bg-white/50' : ''}`}>
                        <td className="px-6 py-3 text-gray-700 font-medium">{row.aspect}</td>
                        <td className="px-6 py-3 text-[#1B6B3A] font-semibold">{row.ca}</td>
                        <td className="px-6 py-3 text-gray-500">{row.fed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════ ENVIRONMENTAL IMPACT ════════════════════════ */}
        <section className="py-16 bg-[#0F3D22]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-12">
              <div className="text-[#4CAF82] text-xs font-bold uppercase tracking-widest mb-3">
                Why It Matters
              </div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 leading-tight">
                The Environmental Impact of E-Waste
              </h2>
              <p className="text-green-200 text-sm max-w-2xl mx-auto leading-relaxed">
                Improper disposal of electronic waste has devastating consequences for human health and the environment. Here is the data that drives responsible recycling.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {environmentalImpacts.map((item) => (
                <div key={item.label} className="bg-white/10 border border-white/10 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#4CAF82]/20 rounded-xl mx-auto mb-3">
                    <i className={`${item.icon} text-[#4CAF82] text-xl`} />
                  </div>
                  <div className="text-2xl md:text-3xl font-extrabold text-white mb-1">
                    {item.stat}
                  </div>
                  <p className="text-green-200 text-xs leading-relaxed">{item.label}</p>
                </div>
              ))}
            </div>

            {/* Impact details */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  title: 'Toxic Chemical Leaching',
                  desc: 'When electronics break down in landfills, toxic chemicals leach into soil and groundwater. Lead from CRT glass, mercury from switches, and cadmium from batteries contaminate drinking water sources. A single cell phone battery can pollute 600,000 liters of water.',
                  icon: 'ri-drop-line',
                },
                {
                  title: 'Air Pollution from Burning',
                  desc: 'Informal e-waste recycling in developing countries often involves open burning of circuit boards and wires. This releases dioxins, furans, and heavy metal particles into the air — some of the most toxic chemicals known to science, linked to cancer and birth defects.',
                  icon: 'ri-windy-line',
                },
                {
                  title: 'Loss of Precious Metals',
                  desc: 'One metric ton of circuit boards contains 200–400 grams of gold. A ton of gold ore from a mine typically contains 3–5 grams of gold. E-waste is literally richer in gold than gold mines. Throwing it away wastes an incredible resource.',
                  icon: 'ri-coins-line',
                },
                {
                  title: 'Rare Earth Mining Destruction',
                  desc: 'Mining rare earth elements for new electronics produces radioactive waste, toxic sludge, and destroys habitats. Recycling e-waste recovers neodymium, dysprosium, and lanthanum — reducing the need for destructive new mining operations.',
                  icon: 'ri-earth-line',
                },
                {
                  title: 'Climate Impact',
                  desc: 'Manufacturing a single laptop generates approximately 200 kg of CO2. Recycling electronics and recovering materials reduces the energy needed for new production by 70–90%. Recycling one million laptops saves energy equal to 3,500 U.S. homes per year.',
                  icon: 'ri-sun-line',
                },
                {
                  title: 'Human Health Risks',
                  desc: 'Workers in informal e-waste recycling — often children in developing countries — are exposed to lead, mercury, cadmium, and flame retardants. These cause neurological damage, respiratory disease, reproductive harm, and increased cancer rates.',
                  icon: 'ri-heart-pulse-line',
                },
              ].map((impact) => (
                <div key={impact.title} className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#4CAF82]/20 rounded-lg mb-3">
                    <i className={`${impact.icon} text-[#4CAF82] text-lg`} />
                  </div>
                  <h3 className="text-white font-extrabold text-sm mb-2">{impact.title}</h3>
                  <p className="text-green-200/80 text-xs leading-relaxed">{impact.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════ HOW TO RECYCLE ════════════════════════ */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <SectionHeading
              eyebrow="Responsible Recycling"
              title="How to Recycle E-Waste in 6 Steps"
              subtitle="Recycling electronics is straightforward when you use a certified facility. Here is the complete process from drop-off to material recovery."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {recyclingSteps.map((item, idx) => (
                <div
                  key={item.step}
                  className="border border-gray-200 rounded-xl p-5 hover:border-[#1B6B3A]/30 transition-all duration-200 relative"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#1B6B3A] rounded-full text-white text-sm font-extrabold">
                      {item.step}
                    </div>
                    <div className="w-8 h-8 flex items-center justify-center bg-[#E8F5EE] rounded-lg">
                      <i className={`${item.icon} text-[#1B6B3A] text-sm`} />
                    </div>
                  </div>
                  <h3 className="text-sm font-extrabold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                  {idx === 0 && (
                    <div className="mt-4 flex flex-col gap-2">
                      <Link
                        to="/dropoff-form"
                        className="inline-flex items-center gap-1.5 text-[#1B6B3A] text-xs font-semibold hover:underline cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-map-pin-line" />
                        Find our drop-off locations
                      </Link>
                      <Link
                        to="/pickup-request"
                        className="inline-flex items-center gap-1.5 text-[#1B6B3A] text-xs font-semibold hover:underline cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-truck-line" />
                        Request free business pickup
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════ FAQ ════════════════════════ */}
        <section className="py-16 bg-[#F4FAF7]">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <SectionHeading
              eyebrow="Common Questions"
              title="E-Waste FAQ"
              subtitle="Answers to the most frequently asked questions about electronic waste, recycling laws, and environmental impact."
            />
            <FAQAccordion />
          </div>
        </section>

        {/* ════════════════════════ RELATED RESOURCES ════════════════════════ */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <SectionHeading
              eyebrow="Learn More"
              title="Related Resources & Guides"
              subtitle="Explore more of our recycling guides and resources for Los Angeles residents and businesses."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  title: 'TV Recycling Guide',
                  desc: 'How to recycle CRT, LCD, and LED televisions in Los Angeles. What CalRecycle requires and what to bring.',
                  link: '/tv-recycling',
                  icon: 'ri-tv-line',
                },
                {
                  title: 'Computer Recycling Guide',
                  desc: 'Desktop and laptop recycling in LA. Data security, accepted models, and the recycling process.',
                  link: '/computer-recycling',
                  icon: 'ri-computer-line',
                },
                {
                  title: 'Battery Recycling Guide',
                  desc: 'Lithium-ion, lead-acid, and UPS battery recycling. Safety tips and what Greenly accepts.',
                  link: '/battery-recycling',
                  icon: 'ri-battery-charge-line',
                },
                {
                  title: 'Hard Drive Destruction',
                  desc: 'Certified data destruction with Certificates of Destruction. HIPAA-compliant hard drive shredding.',
                  link: '/hard-drive-destruction',
                  icon: 'ri-hard-drive-line',
                },
                {
                  title: 'Blog & Resources',
                  desc: 'E-waste recycling tips, community event guides, and in-depth articles for LA residents.',
                  link: '/blog',
                  icon: 'ri-article-line',
                },
                {
                  title: 'Corporate Sustainability',
                  desc: 'ESG-compliant recycling programs, Certificates of Destruction, and business documentation.',
                  link: '/sustainability-report',
                  icon: 'ri-bar-chart-box-line',
                },
                {
                  title: 'Items We Recycle & FAQ',
                  desc: 'Full list of accepted and not-accepted items. Answers to 20+ common recycling questions.',
                  link: '/faq',
                  icon: 'ri-question-answer-line',
                },
              ].map((resource) => (
                <Link
                  key={resource.link}
                  to={resource.link}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#1B6B3A]/30 hover:bg-[#F4FAF7] transition-all duration-200 group cursor-pointer"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#E8F5EE] rounded-lg group-hover:bg-[#1B6B3A] transition-colors">
                      <i className={`${resource.icon} text-[#1B6B3A] text-sm group-hover:text-white transition-colors`} />
                    </div>
                    <h3 className="text-sm font-extrabold text-gray-900 group-hover:text-[#1B6B3A] transition-colors">
                      {resource.title}
                    </h3>
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed">{resource.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════ CTA ════════════════════════ */}
        <section className="py-14 bg-[#1B6B3A]">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
              Ready to Recycle Your E-Waste?
            </h2>
            <p className="text-green-100 text-sm md:text-base mb-8 max-w-xl mx-auto leading-relaxed">
              Free drop-off at two Los Angeles locations. Free business pickup throughout LA County. CalRecycle certified and environmentally responsible.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/dropoff-form"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#1B6B3A] font-bold px-8 py-3 rounded-md hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-map-pin-line" />
                Free Drop-Off
              </Link>
              <Link
                to="/pickup-request"
                className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-truck-line" />
                Request Free Pickup
              </Link>
              <a
                href="tel:+12136001000"
                className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-phone-line" />
                Call (213) 600-1000
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
};

export default EwasteGuidePage;